import React from 'react'
import {
  LuClock,
  LuFileSpreadsheet,
  LuRectangleEllipsis,
  LuSend,
} from 'react-icons/lu'
import { useNavigate } from 'react-router'

import AddReferralModalOption from '@/components/modals/AddReferralModal/components/AddReferralModalOption'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAddReferralsModal } from '@/hooks/useAddReferralsModal'
import useAppSelector from '@/hooks/useAppSelector'
import { useImportHistoricalReferralsModal } from '@/hooks/useImportHistoricalReferralsModal'
import { useIsRoute } from '@/hooks/useIsRoute'
import { useLogHistoricalReferralModal } from '@/hooks/useLogHistoricalReferralModal'
import useNotyf from '@/hooks/useNotyf'
import { usePrefetchReferralSetupQuery } from '@/hooks/usePrefetchReferralSetupQuery'
import { REFERRALS_IMPORT_STEP } from '@/lib/constants'
import { ROUTES } from '@/lib/routes'
import { cn } from '@/lib/utils'
import { selectReferralsImportHistorical } from '@/redux/slices/referralsImportHistorical'

const AddReferralModal: React.FC = () => {
  const referralsImportHistoricalState = useAppSelector(
    selectReferralsImportHistorical,
  )
  const navigate = useNavigate()
  const addReferralsModal = useAddReferralsModal()
  const importHistoricalReferralsModal = useImportHistoricalReferralsModal()
  const logHistoricalReferralModal = useLogHistoricalReferralModal()
  const prefetchReferralSetupQuery = usePrefetchReferralSetupQuery()
  const isRoute = useIsRoute()
  const notyf = useNotyf()

  const iconClassName = 'text-xl sm:text-2xl'

  return (
    <Dialog
      open={addReferralsModal.isShowing}
      onHide={addReferralsModal.hideModal}
    >
      <DialogContent className="sm:max-w-2xl bg-background p-6">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1.5">
            <div
              className="size-10 rounded-full bg-primary/10 flex items-center
                justify-center"
            >
              <LuClock className="text-xl text-primary" />
            </div>
            <DialogTitle>Add referrals</DialogTitle>
          </div>
        </DialogHeader>

        <DialogBody>
          <p className="text-muted-foreground mb-4">
            Send a new referral to a partner, or upload and log your existing
            referral history.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <AddReferralModalOption
              icon={<LuSend className={cn(iconClassName, 'text-primary')} />}
              title="Send New"
              subtitle="Refer a client to a partner"
              isHighlighted
              onMouseOver={prefetchReferralSetupQuery}
              onClick={() => {
                if (isRoute(ROUTES._referClient)) {
                  notyf.open({
                    type: 'info',
                    message: "You're already on the send new referral page",
                    duration: 5000,
                  })

                  addReferralsModal.hideModal()
                  return
                }

                navigate(ROUTES.referClient())
              }}
            />

            <div className="hidden sm:block w-px bg-border self-stretch"></div>

            <AddReferralModalOption
              icon={
                <LuFileSpreadsheet
                  className={cn(iconClassName, 'text-primary')}
                />
              }
              title="Upload Multiple"
              subtitle={
                <>
                  From a spreadsheet
                  {referralsImportHistoricalState.step ===
                    REFERRALS_IMPORT_STEP.resolveImport && (
                    <div className="text-muted-foreground/70">(continue)</div>
                  )}
                </>
              }
              onClick={() => {
                addReferralsModal.hideModal()
                importHistoricalReferralsModal.show()
              }}
            />

            <AddReferralModalOption
              icon={
                <LuRectangleEllipsis
                  className={cn(iconClassName, 'text-gray-600')}
                />
              }
              title="Log Single"
              subtitle="Using our form"
              onClick={() => {
                addReferralsModal.hideModal()
                logHistoricalReferralModal.show()
              }}
            />
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

export default AddReferralModal
